// The estimated soil surface around a field node's pipe, as the backend's radar/soil/ endpoint reports it: where the
// pipe wall's echo steps up as it enters the soil, from the node's dry bursts. A second-order check of how deep the
// pipe was planted, to be taken with a grain of salt: the depth the farmers report stays the reference. Said from the
// top of the pipe (the soil left inside it varies with how the hole was dug, so its floor is no fixed reference): the
// radar measures from a little inside the top (radar_zero_mm, measured or assumed). Pure helpers.

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
export const SOIL_LINE_COLOR = "#f4f1e8";
const cm = (mm) => `${Math.round(mm / 10)} cm`;

// The endpoint's answer, checked: anything that is not it reads as null.
export function readSoil(data) {
  if (!data || typeof data !== "object") return null;
  const status = ["ok", "weak", "none"].includes(data.status) ? data.status : null;
  if (!status) return null;
  return {
    status,
    soilMm: num(data.soil_mm),
    aboveFloorMm: num(data.above_floor_mm),
    confidence: num(data.confidence),
    reportedMm: num(data.reported_above_floor_mm),
    belowTopMm: num(data.below_top_mm),
    inGroundMm: num(data.in_ground_mm),
    radarZeroMm: num(data.radar_zero_mm),
    radarZeroMeasured: data.radar_zero_measured === true,
    check: data.check === true,
    reasons: Array.isArray(data.reasons) ? data.reasons.filter((r) => typeof r === "string") : [],
    days: num(data.days),
  };
}

// One line under the heatmap, from the top of the pipe; a server without below_top_mm is said from the floor as before.
export function soilText(soil) {
  if (!soil) return "";
  if (soil.status === "none" || soil.soilMm === null)
    return `No soil surface found${soil.reasons[0] ? `: ${soil.reasons[0]}` : "."}`;
  const weak = soil.status === "weak" ? " (weak)" : "";
  const reported = soil.reportedMm !== null ? ` (reported ${cm(soil.reportedMm)})` : "";
  const confidence = soil.confidence !== null ? ` Confidence ${Math.round(soil.confidence)}.` : "";
  const check = soil.check ? " Far from the reported depth: ask the farmers to check." : "";
  if (soil.belowTopMm === null) {
    if (soil.aboveFloorMm === null) return `No soil surface found.`;
    return `Soil surface, estimated${weak}: ${cm(soil.aboveFloorMm)} above the pipe floor${reported}.${confidence}${check}`;
  }
  const ground = soil.inGroundMm !== null ? `, so the pipe is about ${cm(soil.inGroundMm)} in the ground${reported}` : "";
  return `Soil surface, estimated${weak}: ${cm(soil.belowTopMm)} below the top of the pipe${ground}.${confidence}${check}`;
}

// Where those numbers come from, in one line: the radar's own distance and its zero inside the pipe's top.
export function soilRadarText(soil) {
  if (!soil || soil.status === "none" || soil.soilMm === null || soil.belowTopMm === null || soil.radarZeroMm === null)
    return "";
  const zero = soil.radarZeroMm % 10 === 0 ? cm(soil.radarZeroMm) : `${(soil.radarZeroMm / 10).toFixed(1)} cm`;
  return `That is ${cm(soil.soilMm)} from the radar, which measures from ${zero} inside the top${
    soil.radarZeroMeasured ? " (measured)" : " (assumed: not measured on this node)"
  }.`;
}

// The dashed line for RadarWaterfall's lines prop, or null when there is nothing to draw.
export function soilLine(soil) {
  if (!soil || soil.status === "none" || soil.soilMm === null) return null;
  return { mm: soil.soilMm, label: "Soil surface (estimated)", color: SOIL_LINE_COLOR };
}
